import { $connectedServer, setConnectedServer } from '@/stores/connectedServer';
import { closeDialog } from '@/stores/dialog';
import { $serverState } from '@/stores/server';
import {
  type ServerInfo,
  decodeConnectionKey,
} from '@/utils/connectionKeyUtils';
import { IS_TAURI } from '@/utils/platformUtils';
import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { toast } from 'react-toastify';

export function ConnectServerDialog() {
  const connectedServer = useStore($connectedServer);
  const serverState = useStore($serverState);
  const [key, setKey] = useState('');
  const [ip, setIp] = useState('');
  const [port, setPort] = useState<number | ''>('');
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleDecode = () => {
    setStatus(null);
    const info = decodeConnectionKey(key.trim());
    if (!info) {
      setStatus('Invalid connection key');
      toast.error('Invalid connection key');
      return;
    }
    setIp(info.ip);
    setPort(info.port);
    setStatus('Decoded connection key');
  };

  const buildUrl = (srv: ServerInfo) => {
    const proto = srv.port === 443 ? 'https' : 'http';
    const host = srv.ip.includes(':') ? `[${srv.ip}]` : srv.ip;
    return `${proto}://${host}:${srv.port}/health`;
  };

  const handleConnect = async () => {
    setStatus(null);

    if (IS_TAURI && serverState) {
      setStatus('Stop your local hosted server before connecting to another.');
      toast.error(
        'Stop your local hosted server before connecting to another.',
      );
      return;
    }

    let srv: ServerInfo | undefined = undefined;
    if (key.trim()) {
      srv = decodeConnectionKey(key.trim());
      if (!srv) {
        setStatus('Invalid connection key');
        toast.error('Invalid connection key');
        return;
      }
    } else if (ip.trim() && port !== '') {
      srv = { ip: ip.trim(), port: Number(port) };
    } else {
      setStatus('Provide a connection key or IP and port');
      toast.error('Provide a connection key or IP and port');
      return;
    }

    setLoading(true);
    try {
      const url = buildUrl(srv);
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(id);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setConnectedServer(srv);
      setStatus('Connected: healthy');
      closeDialog();
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setStatus('Connection timed out');
        toast.error('Connection timed out');
      } else {
        const message = `Connection failed: ${err instanceof Error ? err.message : String(err)}`;
        setStatus(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-2xl font-bold">Connect to Server</h2>

        {connectedServer && (
          <div className="mb-4 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
            <p className="font-semibold">Currently connected</p>
            <p className="font-mono">
              {connectedServer.ip}:{connectedServer.port}
            </p>
            <button
              className="mt-2 rounded bg-blue-700 px-3 py-1 text-white hover:bg-blue-800"
              onClick={() => {
                setConnectedServer(null);
                setStatus('Disconnected');
              }}
              type="button"
            >
              Disconnect
            </button>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Connection Key
            </label>
            <input
              className="mt-1 w-full rounded border px-3 py-2"
              value={key}
              onChange={e => setKey(e.target.value)}
              placeholder="Paste connection key here"
            />
            <div className="mt-2 flex gap-2">
              <button
                className="rounded bg-gray-200 px-3 py-2 text-sm font-medium hover:bg-gray-300"
                onClick={handleDecode}
                type="button"
              >
                Decode
              </button>
              <button
                className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                onClick={handleConnect}
                disabled={loading}
                type="button"
              >
                {loading ? 'Connecting...' : 'Connect (key)'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              IP Address
            </label>
            <input
              className="mt-1 w-full rounded border px-3 py-2 font-mono"
              value={ip}
              onChange={e => setIp(e.target.value)}
              placeholder="e.g. 198.51.100.5 or 2001:db8::1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Port
            </label>
            <input
              className="mt-1 w-full rounded border px-3 py-2 font-mono"
              value={port}
              onChange={e =>
                setPort(e.target.value === '' ? '' : Number(e.target.value))
              }
              placeholder="e.g. 8080"
            />
            <div className="mt-2 flex gap-2">
              <button
                className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                onClick={handleConnect}
                disabled={loading}
                type="button"
              >
                {loading ? 'Connecting...' : 'Connect'}
              </button>
              <button
                className="rounded bg-gray-200 px-3 py-2 text-sm font-medium hover:bg-gray-300"
                onClick={() => {
                  setKey('');
                  setIp('');
                  setPort('');
                  setStatus(null);
                }}
                type="button"
              >
                Reset
              </button>
            </div>
          </div>

          {status && <p className="text-sm text-gray-700">{status}</p>}
        </div>

        <div className="mt-6 flex gap-2">
          <button
            className="ml-auto rounded bg-gray-300 px-4 py-2 font-semibold hover:bg-gray-400"
            onClick={closeDialog}
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
